// Client Portal — SLM (custom model) portal. Extracted from App.js.
import { useState, useEffect, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { authFetch } from "../lib/api";

export const SLMPortal = ({ facility, user }) => {
  const [tab, setTab] = useState("query");
  const [trainingData, setTrainingData] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [models, setModels] = useState([]);
  const [, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  // Query state
  const [prompt, setPrompt] = useState("");
  const [queryResult, setQueryResult] = useState(null);
  const [querying, setQuerying] = useState(false);
  const [selectedData, setSelectedData] = useState(new Set());

  const fid = facility.facility_id;

  const fetchData = useCallback(async () => {
    setLoading(true);
    let url = `/my-training-data?facility_id=${encodeURIComponent(fid)}`;
    if (statusFilter) url += `&status=${encodeURIComponent(statusFilter)}`;
    const [td, j, m] = await Promise.all([
      authFetch(url),
      authFetch(`/my-training-jobs?facility_id=${encodeURIComponent(fid)}`),
      authFetch(`/my-models?facility_id=${encodeURIComponent(fid)}`),
    ]);
    setTrainingData(Array.isArray(td) ? td : []);
    setJobs(Array.isArray(j) ? j : []);
    setModels(Array.isArray(m) ? m : []);
    setLoading(false);
  }, [fid, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBulkReview = async (action) => {
    if (selectedData.size === 0) return;
    if (!window.confirm(`${action} ${selectedData.size} item(s)?`)) return;
    await authFetch("/my-training-data/review", {
      method: "POST",
      body: JSON.stringify({ ids: [...selectedData], action }),
    });
    setSelectedData(new Set());
    fetchData();
  };

  const queryModel = async () => {
    if (!prompt.trim()) return;
    setQuerying(true);
    setQueryResult(null);
    try {
      const data = await authFetch("/slm/query", {
        method: "POST",
        body: JSON.stringify({
          facility_id: fid,
          prompt: prompt.trim(),
          max_tokens: 512,
          temperature: 0.7,
        }),
      });
      setQueryResult(data);
    } catch (err) {
      setQueryResult({ error: err.message });
    }
    setQuerying(false);
  };

  const pendingCount = trainingData.filter(
    (d) => d.validation_status === "pending",
  ).length;
  const approvedCount = trainingData.filter(
    (d) => d.validation_status === "approved",
  ).length;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-amber-700">
            {pendingCount}
          </div>
          <div className="text-xs text-amber-600">Pending Review</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-700">
            {approvedCount}
          </div>
          <div className="text-xs text-green-600">Approved</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-700">{jobs.length}</div>
          <div className="text-xs text-blue-600">Training Jobs</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-700">
            {models.length}
          </div>
          <div className="text-xs text-purple-600">Models</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
        {["query", "training-data", "jobs", "models"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm rounded-md ${tab === t ? "bg-white shadow text-purple-700 font-medium" : "text-slate-600 hover:text-slate-800"}`}
          >
            {t === "query"
              ? "Query Model"
              : t === "training-data"
                ? "Training Data"
                : t === "jobs"
                  ? "Jobs"
                  : "Models"}
          </button>
        ))}
      </div>

      {/* Query Model Tab */}
      {tab === "query" && (
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">
            Query Your Custom Model
          </h3>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder="Enter your prompt..."
            className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 mb-3"
          />
          <button
            onClick={queryModel}
            disabled={querying || !prompt.trim()}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${querying ? "bg-slate-200 text-slate-400" : "bg-purple-600 text-white hover:bg-purple-700"}`}
          >
            {querying ? "Querying..." : "Send Query"}
          </button>
          {queryResult && (
            <div
              className={`mt-4 p-4 rounded-lg text-sm ${queryResult.error ? "bg-red-50 border border-red-200 text-red-700" : "bg-slate-50 border border-slate-200"}`}
            >
              {queryResult.error ? (
                queryResult.error
              ) : (
                <>
                  <p className="whitespace-pre-wrap">
                    {queryResult.response ||
                      queryResult.output ||
                      JSON.stringify(queryResult)}
                  </p>
                  {queryResult.inference_time_ms && (
                    <p className="text-xs text-slate-400 mt-2">
                      {queryResult.inference_time_ms}ms |{" "}
                      {queryResult.tokens_used} tokens
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Training Data Tab */}
      {tab === "training-data" && (
        <div className="bg-white rounded-xl shadow-md border border-slate-200">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-slate-300 rounded text-sm"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              {selectedData.size > 0 && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleBulkReview("approved")}
                    className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Approve ({selectedData.size})
                  </button>
                  <button
                    onClick={() => handleBulkReview("rejected")}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Reject ({selectedData.size})
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={fetchData}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 w-8">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked)
                          setSelectedData(
                            new Set(trainingData.map((d) => d.id)),
                          );
                        else setSelectedData(new Set());
                      }}
                      className="h-4 w-4"
                    />
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                    Instruction
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                    Response
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                    Quality
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {trainingData.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selectedData.has(d.id)}
                        onChange={() =>
                          setSelectedData((prev) => {
                            const n = new Set(prev);
                            n.has(d.id) ? n.delete(d.id) : n.add(d.id);
                            return n;
                          })
                        }
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-700 max-w-xs truncate">
                      {d.instruction}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500 max-w-xs truncate">
                      {d.response}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {d.quality_score || "-"}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`px-2 py-0.5 text-xs rounded-full ${
                          d.validation_status === "approved"
                            ? "bg-green-100 text-green-700"
                            : d.validation_status === "rejected"
                              ? "bg-red-100 text-red-700"
                              : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {d.validation_status}
                      </span>
                    </td>
                  </tr>
                ))}
                {trainingData.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-sm text-slate-400"
                    >
                      No training data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Jobs Tab */}
      {tab === "jobs" && (
        <div className="bg-white rounded-xl shadow-md border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                  Job ID
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                  Type
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                  Progress
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.map((j) => (
                <tr key={j.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-xs font-mono text-slate-600">
                    {j.id}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {j.job_type}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${
                        j.job_status === "completed"
                          ? "bg-green-100 text-green-700"
                          : j.job_status === "failed"
                            ? "bg-red-100 text-red-700"
                            : j.job_status === "running"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {j.job_status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {j.progress_percent != null
                      ? `${j.progress_percent}%`
                      : "-"}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-400">
                    {j.created_at?.split("T")[0]}
                  </td>
                </tr>
              ))}
              {jobs.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-sm text-slate-400"
                  >
                    No training jobs.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Models Tab */}
      {tab === "models" && (
        <div className="bg-white rounded-xl shadow-md border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                  Model
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                  Base
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {models.map((m) => (
                <tr key={m.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-sm font-medium text-slate-700">
                    {m.model_path || m.hf_model_path || `v${m.version}`}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {m.base_model_id}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${
                        m.deployment_status === "production"
                          ? "bg-green-100 text-green-700"
                          : m.deployment_status === "staging"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {m.deployment_status || "draft"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-400">
                    {m.created_at?.split("T")[0]}
                  </td>
                </tr>
              ))}
              {models.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-8 text-center text-sm text-slate-400"
                  >
                    No models trained yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// PRIVATE LLM — Chat interface to Ollama/hosted model
// ═══════════════════════════════════════════════════════════════════
